import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  LinearProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { fToNow } from '../../../utils/formatTime';
import { useSessionContext } from '../../../auth/useSessionContext';
import { IconButtonAnimate } from '../../../components/animate';
import Iconify from '../../../components/iconify';
import MenuPopover from '../../../components/menu-popover';
import Scrollbar from '../../../components/scrollbar';
import useNotifications from '../../../hooks/useNotifications';

export default function NotificationsPopover() {
  const { user } = useSessionContext();
  const isStudentPath = user?.viewPageAs === 'student';
  const [openPopover, setOpenPopover] = useState(null);

  const { notifications, fetching, readAll, unread } = useNotifications({
    page: 1,
    background: true,
  });

  const normalizedNotifications = useMemo(
    () =>
      (notifications || []).map((n) => ({
        ...n,
        id: n?.id != null ? String(n.id) : '',
      })),
    [notifications]
  );

  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
    if (unread > 0) readAll();
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const navigate = useNavigate();

  const handleNotificationClick = (notification) => {
    if (!notification.link) return;
    navigate(notification.link);
    handleClosePopover();
  };

  return (
    <>
      <IconButtonAnimate
        color={openPopover ? 'primary' : 'default'}
        onClick={handleOpenPopover}
        aria-label="Notificações"
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={unread} color="error">
          <Iconify icon="eva:bell-fill" />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 360, p: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notificações</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Você possui {unread} mensagens não lidas
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />
        {fetching && <LinearProgress />}

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                Novas
              </ListSubheader>
            }
          >
            {normalizedNotifications.slice(0, 2).map((notification) => (
              <NotificationItem
                key={notification.id || notification.createdAt}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
              />
            ))}
          </List>

          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                Anteriores
              </ListSubheader>
            }
          >
            {normalizedNotifications.slice(2, 5).map((notification) => (
              <NotificationItem
                key={notification.id || notification.createdAt}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
              />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            disableRipple
            onClick={() => {
              navigate(isStudentPath ? '/student/notifications' : '/dashboard/notifications');
              handleClosePopover();
            }}
          >
            Ver todas
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string,
    avatar: PropTypes.node,
    type: PropTypes.string,
    title: PropTypes.string,
    read: PropTypes.bool,
    description: PropTypes.string,
    link: PropTypes.string,
    createdAt: PropTypes.string,
  }),
  onClick: PropTypes.func,
};

function NotificationItem({ notification, onClick }) {
  const { avatar, title } = renderContent(notification);

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        // Notificação sem link (antiga ou de outro tipo) não navega; sem isto o
        // ListItemButton mostraria o cursor de pointer mesmo assim.
        cursor: notification.link ? 'pointer' : 'default',
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>

      <ListItemText
        disableTypography
        primary={title}
        secondary={
          <Stack direction="row" sx={{ mt: 0.5, typography: 'caption', color: 'text.disabled' }}>
            <Iconify icon="eva:clock-fill" width={16} sx={{ mr: 0.5 }} />
            <Typography variant="caption">{fToNow(notification.createdAt)}</Typography>
          </Stack>
        }
      />
    </ListItemButton>
  );
}

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {notification.description}
      </Typography>
    </Typography>
  );

  if (notification.type === 'order_placed') {
    return {
      avatar: <img alt={notification.title} src="/assets/icons/notification/ic_package.svg" />,
      title,
    };
  }
  if (notification.type === 'order_shipped') {
    return {
      avatar: <img alt={notification.title} src="/assets/icons/notification/ic_shipping.svg" />,
      title,
    };
  }
  if (notification.type === 'mail') {
    return {
      avatar: <img alt={notification.title} src="/assets/icons/notification/ic_mail.svg" />,
      title,
    };
  }
  if (notification.type === 'chat_message') {
    return {
      avatar: <img alt={notification.title} src="/assets/icons/notification/ic_chat.svg" />,
      title,
    };
  }
  return {
    avatar: notification.avatar ? <img alt={notification.title} src={notification.avatar} /> : null,
    title,
  };
}
